import { useParams } from 'react-router-dom';

import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Text,
  Tooltip,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';

import ValidationMessage from 'components/validation/ValidationMessage';

import SharedTextFieldInput from 'components/fields/shared/TextFieldInput';
import ValidateFile from 'components/fields/shared/Message/components/ValidateFile';
import ValidateText from 'components/fields/shared/Message/components/ValidateText';

import useFormBinderDisabled from 'hooks/useFormBinderDisabled';

import { FILE_TYPE } from 'variables/constants';

import { useGapInsuranceResolver, LAPSE_REASON_LABELS } from './hooks';
import { getHTMLId, useResolverModal } from './resolverUtils';
import type { ValidationResolverProps, LapseReasonOption } from './types';

export interface Validation090aResolverProps extends ValidationResolverProps {}

const VALIDATION_ID = '090a';
const FORM_STATE_KEY = 'autoPersonalForm';

const REASON_OPTIONS: LapseReasonOption[] = ['a', 'b', 'c', 'd'];

const Validation090aResolver = ({
  section,
  itemKey: propItemKey,
  compact = false,
}: Validation090aResolverProps) => {
  const { uuid } = useParams();
  const isBinderDisabled = useFormBinderDisabled(FORM_STATE_KEY);
  const {
    isOpen: isCompactOpen,
    onOpen: onCompactOpen,
    onClose: onCompactClose,
  } = useDisclosure();

  const itemKey = propItemKey || 'global';

  const { state, handlers, fieldIds, alertMessage, getCompactAlertStatus } =
    useGapInsuranceResolver({
      validationId: VALIDATION_ID,
      section,
      itemKey,
      formStateKey: FORM_STATE_KEY,
      fileType: FILE_TYPE.TYPE_090a.type,
    });

  const htmlId = getHTMLId(VALIDATION_ID, itemKey);

  // Use shared modal hook for hash-based navigation
  const { handleModalClose } = useResolverModal(
    compact,
    Object.values(fieldIds),
    htmlId,
    onCompactOpen,
    onCompactClose,
  );

  if (!state.show) return null;

  // Compact mode: show button that opens modal
  if (compact) {
    return (
      <>
        {/* Anchor divs for mandatory field navigation */}
        <div id={htmlId}></div>
        <div id={fieldIds.reasonOption}></div>
        <div id={fieldIds.otherExplanation}></div>
        <div id={fieldIds.noClaimsConfirmed}></div>
        <Tooltip placement="top" label={alertMessage}>
          <Alert status={getCompactAlertStatus()} mt="10px" fontSize="14px">
            <Button
              onClick={() => onCompactOpen()}
              w="100%"
              whiteSpace="normal"
            >
              <AlertIcon />
              Add remark
            </Button>
          </Alert>
        </Tooltip>
        <Modal
          isOpen={isCompactOpen}
          onClose={handleModalClose}
          size="4xl"
          scrollBehavior="inside"
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Gap in Insurance</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Box mb="10px">
                <Validation090aResolver
                  section={section}
                  itemKey={itemKey}
                  compact={false}
                />
              </Box>
              <Button
                variant="brand"
                onClick={handleModalClose}
                mb="15px"
                float="right"
              >
                Close
              </Button>
            </ModalBody>
          </ModalContent>
        </Modal>
      </>
    );
  }

  return (
    <Box>
      {/* Anchor divs for mandatory field navigation */}
      <div id={htmlId}></div>
      <div id={fieldIds.reasonOption}></div>
      <div id={fieldIds.otherExplanation}></div>
      <div id={fieldIds.noClaimsConfirmed}></div>

      <Alert status="warning" mt="10px" fontSize="14px">
        <AlertIcon />
        <ValidationMessage message={alertMessage} />
      </Alert>

      <Box mt="15px" p="15px" bg="gray.50" borderRadius="md">
        {/* Reason for Lapse */}
        <Text fontWeight="medium" mb="8px">
          Was the client cancelled by the insurer prior to the lapse for any of
          the following reasons?
        </Text>
        <VStack
          align="stretch"
          spacing="8px"
          mb="12px"
          id={fieldIds.reasonOption}
        >
          {REASON_OPTIONS.map((option) => (
            <Checkbox
              key={option}
              isChecked={state.reasonOption === option}
              onChange={handlers.handleReasonOptionChange(option)}
              isDisabled={isBinderDisabled}
            >
              {option}. {LAPSE_REASON_LABELS[option]}
            </Checkbox>
          ))}
        </VStack>

        {/* Other Explanation (shown when "d" is selected) */}
        {state.reasonOption === 'd' && (
          <Box mb="12px" id={fieldIds.otherExplanation}>
            <SharedTextFieldInput
              label="Other reason for lapse"
              value={state.otherText}
              onChange={handlers.handleOtherTextChange}
              borderColor={
                !state.otherText || state.otherText.trim().length === 0
                  ? 'red'
                  : 'green'
              }
              formStateKey={FORM_STATE_KEY}
              section={section}
            />
          </Box>
        )}

        {state.remark &&
          (state.reasonOption === 'd' ||
            state.remark.status === 'approved') && (
            <ValidateText
              uuid={uuid}
              remark={state.remark}
              validationId={VALIDATION_ID}
              itemKey={itemKey}
              formStateKey={FORM_STATE_KEY}
              context={alertMessage}
            />
          )}

        {/* No Claims Confirmation */}
        <Alert
          status={getCompactAlertStatus()}
          mt="10px"
          fontSize="14px"
          mb="12px"
        >
          <Checkbox
            isChecked={state.noClaimsConfirmed}
            onChange={handlers.handleNoClaimsConfirmedChange}
            isDisabled={isBinderDisabled}
            id={fieldIds.noClaimsConfirmed}
          >
            I have confirmed with the customer there were no claims of any kind
            during the lapse
          </Checkbox>
        </Alert>
        {/* File Upload */}
        {state.file && (
          <ValidateFile
            uuid={uuid}
            file={state.file}
            validationId={VALIDATION_ID}
            itemKey={itemKey}
            formStateKey={FORM_STATE_KEY}
          />
        )}
      </Box>
    </Box>
  );
};

export default Validation090aResolver;
